<template>
  <span
    class="readiness-chip"
    role="group"
    :aria-label="ariaLabel"
    data-testid="readiness-chip"
  >
    <span class="readiness-chip__value">{{ valueText }}</span>
    <span class="readiness-chip__label">Readiness</span>
    <span
      v-if="change !== null"
      class="readiness-chip__change"
      :class="`readiness-chip__change--${trend}`"
      data-testid="readiness-chip-change"
    >
      <svg
        class="readiness-chip__arrow"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path :d="arrowPath" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      <span>{{ changeText }}</span>
    </span>
  </span>
</template>

<script setup>
import { computed } from "vue";

const props = defineProps({
  /** `{ percent, changePercent }` — changePercent is the point change vs last month (null = unknown). */
  readiness: {
    type: Object,
    default: () => ({ percent: 0, changePercent: null }),
  },
});

const ARROW_PATHS = {
  up: "M6 10V2M6 2L2.5 5.5M6 2L9.5 5.5",
  down: "M6 2V10M6 10L2.5 6.5M6 10L9.5 6.5",
  flat: "M2 6H10M10 6L6.5 2.5M10 6L6.5 9.5",
};

const percent = computed(() => Math.round(Number(props.readiness?.percent) || 0));

const change = computed(() => {
  const raw = props.readiness?.changePercent;
  if (raw === null || raw === undefined || !Number.isFinite(Number(raw))) return null;
  return Math.round(Number(raw));
});

const trend = computed(() => {
  if (change.value > 0) return "up";
  if (change.value < 0) return "down";
  return "flat";
});

const valueText = computed(() => `${percent.value}%`);

const changeText = computed(() => {
  if (change.value > 0) return `+${change.value}%`;
  if (change.value < 0) return `-${Math.abs(change.value)}%`;
  return "0%";
});

const arrowPath = computed(() => ARROW_PATHS[trend.value]);

const ariaLabel = computed(() => {
  const base = `Readiness ${valueText.value}`;
  if (change.value === null) return base;
  if (change.value === 0) return `${base}, unchanged vs last month`;
  const direction = change.value > 0 ? "up" : "down";
  return `${base}, ${direction} ${Math.abs(change.value)}% vs last month`;
});
</script>

<style scoped>
.readiness-chip {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 6px;
  height: 22px;
  box-sizing: border-box;
  padding: 1px 8px;
  border: 1px solid #eaecf0;
  border-radius: 1000px;
  background-color: #f9fafb;
  font-family: Inter, sans-serif;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.readiness-chip__value {
  font-weight: 600;
  color: #344054;
}

.readiness-chip__label {
  font-weight: 500;
  color: #475467;
}

.readiness-chip__change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 500;
}

.readiness-chip__change--up {
  color: #067647;
}

.readiness-chip__change--down {
  color: #b42318;
}

.readiness-chip__change--flat {
  color: #475467;
}

.readiness-chip__arrow {
  flex-shrink: 0;
}
</style>
